import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Chip,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  IconButton,
  Fade,
  Grow,
  Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { buildGoogleAuthUrl } from "./Utils/googleAuthUrl";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import HomeHeader from "../Components/HomeHeader";
import HomeFooter from "../Components/HomeFooter";
import defaultPfp from "../Components/globalAssets/ppfLogo.png";
import { niches, region } from "../Utils/constants";
import MuiPhoneInput from "../Components/MuiPhoneInput";
import AlertDialog from "../Components/AlertDialog";
import useAlertDialog from "../Components/useAlertDialog";
import client from "../API";

/* ————————————————— Gmail Scopes Configuration ————————————————— */
const GMAIL_SCOPES = [
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile"
].join(" ");

const REDIRECT_URI = window.location.origin + "/creatorconnect/redirect";

/* ————————————————— Platform Configurations ————————————————— */
const PLATFORMS = {
  Instagram: {
    emoji: '📸',
    color: '#E4405F',
    prefix: '@',
    placeholder: 'yourusername',
    label: 'Instagram Username',
    helper: 'Enter your Instagram handle without the @'
  },
  TikTok: {
    emoji: '🎵',
    color: '#000000',
    prefix: '@',
    placeholder: 'yourusername',
    label: 'TikTok Username',
    helper: 'Enter your TikTok handle without the @'
  },
  YouTube: {
    emoji: '▶️',
    color: '#FF0000',
    prefix: '@',
    placeholder: '@yourchannel',
    label: 'YouTube Handle',
    helper: 'Enter your YouTube handle (must start with @)',
    requiresAt: true
  },
  X: {
    emoji: '✖️',
    color: '#000000',
    prefix: '@',
    placeholder: 'yourusername',
    label: 'X Username',
    helper: 'Enter your X (Twitter) handle without the @'
  },
  Twitch: {
    emoji: '🎮',
    color: '#9146FF',
    prefix: '@',
    placeholder: 'yourusername',
    label: 'Twitch Username',
    helper: 'Enter your Twitch handle without the @'
  },
  Kick: {
    emoji: '🦵',
    color: '#53FC18',
    prefix: '@',
    placeholder: 'yourusername',
    label: 'Kick Username',
    helper: 'Enter your Kick handle without the @'
  },
  Podcast: {
    emoji: '🎙️',
    color: '#8B5CF6',
    prefix: '',
    placeholder: 'Your Podcast Name',
    label: 'Podcast Name',
    helper: 'Enter your podcast name',
    isUrl: true
  }
};

/* ————————————————— Styled Components ————————————————— */
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  paddingTop: 80,
  paddingBottom: 40,
  [theme.breakpoints.down('sm')]: {
    paddingTop: 70,
  }
}));

const ContentCard = styled(Card)(({ theme }) => ({
  maxWidth: 700,
  margin: '0 auto',
  borderRadius: 24,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  overflow: 'visible',
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
    margin: theme.spacing(2),
  }
}));

const PlatformCard = styled(Card)(({ theme, platformcolor }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: `2px solid transparent`,
  borderRadius: 16,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${platformcolor}40`,
    borderColor: platformcolor,
  }
}));

const StepContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

/* ————————————————— Validation Helper ————————————————— */
const validateUsername = (value, platform) => {
  if (!value || value.trim() === '') {
    return { valid: false, message: 'This field is required' };
  }

  const config = PLATFORMS[platform];
  
  // YouTube special handling
  if (config.requiresAt) {
    if (!value.startsWith('@')) {
      return { valid: false, message: 'YouTube handle must start with @' };
    }
    const usernameAfterAt = value.slice(1);
    if (usernameAfterAt.length < 1 || usernameAfterAt.length > 30) {
      return { valid: false, message: 'Invalid YouTube handle length' };
    }
    if (!/^[a-zA-Z0-9_.-]+$/.test(usernameAfterAt)) {
      return { valid: false, message: 'Only letters, numbers, underscores, periods, and hyphens allowed' };
    }
    return { valid: true };
  }

  // For podcasts (name field)
  if (config.isUrl) {
    if (value.length < 2) {
      return { valid: false, message: 'Name too short' };
    }
    return { valid: true };
  }

  // Standard username validation
  if (!/^[a-zA-Z0-9_.]+$/.test(value)) {
    return { valid: false, message: 'Only letters, numbers, underscores, and periods allowed' };
  }
  
  if (value.length < 1 || value.length > 30) {
    return { valid: false, message: 'Username must be 1-30 characters' };
  }

  return { valid: true };
};

/* ————————————————— Main Component ————————————————— */
export default function PartnerOnboard() {
  const navigate = useNavigate();
  const { referrer } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Welcome', 'Basic Info', 'Add Platforms', 'Finish'];

  // Form state
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [creatorNiche, setCreatorNiche] = useState([]);
  const [creatorRegion, setCreatorRegion] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [platforms, setPlatforms] = useState({});
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformValue, setPlatformValue] = useState("");
  const [platformError, setPlatformError] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [referrerData, setReferrerData] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });

  // ✅ NEW: OAuth state for redirect flow
  const [onboardingData, setOnboardingData] = useState(null);

  /* -------------------- Fetch referrer data -------------------- */
  useEffect(() => {
    if (!referrer) return;
    
    (async () => {
      try {
        const resp = await client.users.fetchPublicDetails(referrer);
        const referrerInfo = {
          name: `${resp.first_name || ""} ${resp.last_name || ""}`.trim() || resp.username || "Unknown User",
          avatar: resp.phphref || defaultPfp
        };
        setReferrerData(referrerInfo);
      } catch (err) {
        console.error('Error fetching referrer data:', err);
        setReferrerData({ name: "Unknown User", avatar: defaultPfp });
      }
    })();
  }, [referrer]);

  /* -------------------- Handle OAuth Callback -------------------- */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      openDialog("Error", `Google authorization failed: ${error}`, closeDialog, closeDialog, "Ok");
      // Clean URL
      urlParams.delete('error');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
      return;
    }

    if (code && state) {
      // Retrieve stored onboarding data
      const storedData = sessionStorage.getItem('partner_onboard_data');
      if (!storedData) {
        openDialog("Error", "Session expired. Please try again.", closeDialog, closeDialog, "Ok");
        return;
      }

      const data = JSON.parse(storedData);
      
      // Submit onboarding with Google code
      handleOnboardingSubmit(code, data);

      // Clean URL
      urlParams.delete('code');
      urlParams.delete('state');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  /* -------------------- Handle Onboarding Submit -------------------- */
  const handleOnboardingSubmit = async (googleCode, data) => {
    setLoading(true);

    // Build platform URLs
    const platformUrls = {};
    Object.entries(data.platforms).forEach(([platform, username]) => {
      const config = PLATFORMS[platform];
      if (config.isUrl) {
        platformUrls[platform.toLowerCase()] = username;
      } else if (platform === 'YouTube') {
        platformUrls.youtube = `https://www.youtube.com/${username}`;
      } else {
        const baseUrls = {
          Instagram: 'https://www.instagram.com/',
          TikTok: 'https://www.tiktok.com/@',
          X: 'https://www.x.com/',
          Twitch: 'https://www.twitch.tv/',
          Kick: 'https://www.kick.com/'
        };
        platformUrls[platform.toLowerCase()] = baseUrls[platform] + username;
      }
    });

    const payload = {
      username: data.username,
      region: data.region,
      niche: data.niche,
      supervisor: referrer || null,
      google_code: googleCode,
      phone_number: data.phone,
      ...platformUrls
    };

    try {
      const res = await client.creatorConnect.onboardPartner(payload);
      
      if (res?.success) {
        // Clear stored data
        sessionStorage.removeItem('partner_onboard_data');
        
        setSnack({ 
          open: true, 
          sev: 'success', 
          msg: 'Partner account created successfully! Redirecting to login...' 
        });
        
        setTimeout(() => {
          // Redirect to login page with success message
          navigate(`/login?tab=creator&signup_success=true${referrer ? `&referrer=${referrer}` : ""}`);
        }, 1500);
      } else {
        openDialog("Error", res?.error || "Onboarding failed. Please try again.", closeDialog, closeDialog, "Ok");
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      openDialog("Error", `Onboarding failed: ${err.response?.data?.error || err.message}`, closeDialog, closeDialog, "Ok");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Platform Dialog Handlers -------------------- */
  const handlePlatformClick = (platformName) => {
    setSelectedPlatform(platformName);
    setPlatformValue(platforms[platformName] || "");
    setPlatformError("");
  };

  const handlePlatformSave = () => {
    const validation = validateUsername(platformValue, selectedPlatform);
    if (!validation.valid) {
      setPlatformError(validation.message);
      return;
    }

    setPlatforms({ ...platforms, [selectedPlatform]: platformValue });
    setSelectedPlatform(null);
    setPlatformValue("");
    setPlatformError("");
  };

  const handlePlatformRemove = (platformName) => {
    const newPlatforms = { ...platforms };
    delete newPlatforms[platformName];
    setPlatforms(newPlatforms);
  };

  /* -------------------- Navigation -------------------- */
  const handleNext = () => {
    // Validation for each step
    if (activeStep === 1) {
      if (!username || username.includes(" ")) {
        openDialog("Error", "Username required and cannot contain spaces.", closeDialog, closeDialog, "Ok");
        return;
      }
      if (!creatorRegion || creatorNiche.length === 0) {
        openDialog("Error", "Select region and at least one niche.", closeDialog, closeDialog, "Ok");
        return;
      }
      if (!phone || phone.length < 9) {
        openDialog("Error", "Please enter a valid phone number (minimum 9 digits).", closeDialog, closeDialog, "Ok");
        return;
      }
    }

    if (activeStep === 2) {
      if (Object.keys(platforms).length === 0) {
        openDialog("Error", "Add at least one platform to continue.", closeDialog, closeDialog, "Ok");
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  /* -------------------- Google OAuth Handler (Redirect) -------------------- */
  const handleGoogleConnect = async () => {
    if (!termsAccepted) {
      openDialog("Error", "You must accept the Terms & Conditions to continue.", closeDialog, closeDialog, "Ok");
      return;
    }

    // Store onboarding data in session storage
    const data = {
      username,
      region: creatorRegion,
      niche: creatorNiche,
      phone,
      platforms
    };

    sessionStorage.setItem('partner_onboard_data', JSON.stringify(data));

    // Redirect directly to Google OAuth
    setLoading(true);
    window.location.href = buildGoogleAuthUrl({
      state: 'partner_onboard',
      redirectUri: REDIRECT_URI,
      scope: GMAIL_SCOPES,
    });
  };

  /* -------------------- Step Content Renderers -------------------- */
  const renderWelcomeStep = () => (
    <Fade in timeout={600}>
      <StepContent>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {referrerData && (
            <Grow in timeout={800}>
              <Avatar
                src={referrerData.avatar}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 3,
                  border: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 3
                }}
              />
            </Grow>
          )}
          
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
            Welcome to Blitz! 🚀
          </Typography>
          
          {referrerData && (
            <Chip 
              label={`Invited by ${referrerData.name}`}
              color="primary"
              sx={{ mb: 3, py: 2, px: 1, fontSize: '1rem' }}
            />
          )}
          
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: 500, mx: 'auto' }}>
            Join the Partner Program and let AI help improve your deal flow
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            maxWidth: 500, 
            mx: 'auto',
            textAlign: 'left'
          }}>
            {[
              { icon: '🤖', text: 'AI negotiates deals on your behalf 24/7' },
              { icon: '💰', text: 'Get better rates with automated outreach' },
              { icon: '🌟', text: 'Access to premium brands and campaigns' },
              { icon: '⚡', text: 'Zero manager fees - keep 100% of earnings' }
            ].map((item, idx) => (
              <Grow in timeout={1000 + idx * 200} key={idx}>
                <Card sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '2rem' }}>{item.icon}</Typography>
                    <Typography variant="body1">{item.text}</Typography>
                  </Box>
                </Card>
              </Grow>
            ))}
          </Box>

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            sx={{ mt: 4, py: 1.5, px: 4, borderRadius: 3 }}
          >
            Get Started
          </Button>
        </Box>
      </StepContent>
    </Fade>
  );

  const renderBasicInfoStep = () => (
    <Fade in timeout={600}>
      <StepContent>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Tell us about yourself
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We'll use this to match you with relevant brands
        </Typography>

        <TextField
          fullWidth
          label="Public Username"
          value={username}
          onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
          required
          helperText="This will be your public creator profile name"
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">@</InputAdornment>,
          }}
        />

        <TextField
          fullWidth
          label="Region"
          select
          value={creatorRegion}
          onChange={(e) => setCreatorRegion(e.target.value)}
          required
          helperText="Where are you based?"
          sx={{ mb: 3 }}
        >
          {region.map((r) => (
            <MenuItem key={r} value={r}>
              {r === 'NACAUKAUS' ? 'USA, Canada, UK, Australia' : r}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Content Niche(s)"
          select
          SelectProps={{ multiple: true }}
          value={creatorNiche}
          onChange={(e) => setCreatorNiche(e.target.value)}
          required
          helperText="Select all that apply"
          sx={{ mb: 3 }}
        >
          {niches.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Phone Number *
          </Typography>
          <MuiPhoneInput
            country="us"
            value={phone}
            onChange={setPhone}
            preferredCountries={["us"]}
            isValid={(v) => v.length >= 10}
            required
            fullWidth
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Required for account verification
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
          >
            Continue
          </Button>
        </Box>
      </StepContent>
    </Fade>
  );

  const renderPlatformsStep = () => (
    <Fade in timeout={600}>
      <StepContent>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Add your platforms
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add at least one platform where you create content
        </Typography>

        {/* Added Platforms */}
        {Object.keys(platforms).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Your Platforms
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(platforms).map(([platform, username]) => (
                <Chip
                  key={platform}
                  label={`${PLATFORMS[platform].emoji} ${platform}: ${username}`}
                  onDelete={() => handlePlatformRemove(platform)}
                  color="primary"
                  variant="outlined"
                  sx={{ py: 2.5, fontSize: '0.9rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Available Platforms */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          {Object.keys(platforms).length > 0 ? 'Add More Platforms' : 'Choose Platforms'}
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2
        }}>
          {Object.entries(PLATFORMS).map(([platformName, config]) => (
            <PlatformCard
              key={platformName}
              platformcolor={config.color}
              onClick={() => handlePlatformClick(platformName)}
              sx={{
                opacity: platforms[platformName] ? 0.5 : 1,
                pointerEvents: platforms[platformName] ? 'none' : 'auto'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: config.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}>
                    {config.emoji}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {platformName}
                    </Typography>
                    {platforms[platformName] && (
                      <Chip 
                        size="small" 
                        label="Added" 
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Box>
                  {!platforms[platformName] && <AddIcon />}
                </Box>
              </CardContent>
            </PlatformCard>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            disabled={Object.keys(platforms).length === 0}
          >
            Continue
          </Button>
        </Box>
      </StepContent>
    </Fade>
  );

  const renderFinishStep = () => (
    <Fade in timeout={600}>
      <StepContent>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Almost there! 🎉
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Connect your Google account so Blitz can manage brand emails for you
          </Typography>

          {/* Summary */}
          <Card sx={{ p: 3, mb: 3, textAlign: 'left', backgroundColor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Your Information
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>Username:</strong> @{username}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>Region:</strong> {creatorRegion === 'NACAUKAUS' ? 'USA, Canada, UK, Australia' : creatorRegion}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>Niches:</strong> {creatorNiche.join(', ')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>Phone:</strong> {phone}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>Platforms:</strong> {Object.keys(platforms).length} connected
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Terms */}
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <a 
                  href="/terms_creators" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#667eea', textDecoration: 'none', fontWeight: 600 }}
                >
                  Terms & Conditions
                </a>
              </Typography>
            }
            sx={{ mb: 3, justifyContent: 'center' }}
          />

          {/* ✅ Single Button to Connect Gmail */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGoogleConnect}
            disabled={!termsAccepted || loading}
            sx={{
              py: 1.5,
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6941a5 100%)',
              }
            }}
          >
            {loading ? 'Redirecting to Google...' : 'Connect Gmail & Create Account'}
          </Button>

          <Alert severity="info" sx={{ textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Why Gmail access?</strong> Blitz needs access to your email to process brand partnership emails on your behalf. You'll review all deals before accepting.
            </Typography>
          </Alert>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 3 }}
            disabled={loading}
          >
            Back
          </Button>
        </Box>
      </StepContent>
    </Fade>
  );

  /* -------------------- Platform Input Dialog -------------------- */
  const renderPlatformDialog = () => {
    if (!selectedPlatform) return null;
    
    const config = PLATFORMS[selectedPlatform];
    const validation = platformValue ? validateUsername(platformValue, selectedPlatform) : { valid: false };

    return (
      <Backdrop open={!!selectedPlatform} sx={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <Grow in={!!selectedPlatform}>
          <Card sx={{ 
            maxWidth: 500, 
            width: '90%',
            borderRadius: 4,
            overflow: 'visible'
          }}>
            <Box sx={{ 
              p: 3, 
              background: `linear-gradient(135deg, ${config.color}20, ${config.color}05)`,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  backgroundColor: config.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem'
                }}>
                  {config.emoji}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Add {selectedPlatform}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {config.helper}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <CardContent sx={{ p: 3 }}>
              <TextField
                fullWidth
                autoFocus
                label={config.label}
                value={platformValue}
                onChange={(e) => {
                  let value = e.target.value;
                  
                  // Auto-format YouTube handles
                  if (config.requiresAt && !value.startsWith('@') && value.length > 0) {
                    value = '@' + value;
                  }
                  
                  // Remove @ for other platforms
                  if (!config.requiresAt && !config.isUrl) {
                    value = value.replace(/^@+/, '');
                  }
                  
                  setPlatformValue(value);
                  if (platformError) setPlatformError("");
                }}
                error={!!platformError}
                helperText={platformError || config.helper}
                placeholder={config.placeholder}
                variant="outlined"
                InputProps={{
                  startAdornment: !config.requiresAt && !config.isUrl && (
                    <InputAdornment position="start">
                      <Typography sx={{ color: 'text.secondary' }}>
                        {config.prefix}
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: platformValue && (
                    <InputAdornment position="end">
                      {validation.valid ? (
                        <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
                      ) : (
                        <ErrorOutlineIcon sx={{ color: 'error.main' }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSelectedPlatform(null);
                    setPlatformValue("");
                    setPlatformError("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handlePlatformSave}
                  disabled={!validation.valid}
                  sx={{
                    background: `linear-gradient(135deg, ${config.color}, ${config.color}dd)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${config.color}dd, ${config.color}bb)`,
                    }
                  }}
                >
                  Add Platform
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grow>
      </Backdrop>
    );
  };

  /* -------------------- Main Render -------------------- */
  return (
    <>
      <HomeHeader />
      <PageWrapper>
        <Container maxWidth="md">
          <ContentCard>
            {/* Stepper */}
            <Box sx={{ pt: 4, px: 3 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Step Content */}
            <Box sx={{ minHeight: 400 }}>
              {activeStep === 0 && renderWelcomeStep()}
              {activeStep === 1 && renderBasicInfoStep()}
              {activeStep === 2 && renderPlatformsStep()}
              {activeStep === 3 && renderFinishStep()}
            </Box>
          </ContentCard>
        </Container>
      </PageWrapper>
      <HomeFooter />

      {/* Platform Dialog */}
      {renderPlatformDialog()}

      {/* Loading Backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={loading}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {loading ? 'Redirecting to Google...' : 'Creating your account...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Alert Dialog */}
      <AlertDialog alertState={dialogState} />

      {/* Snackbar */}
      <Snackbar 
        open={snack.open} 
        autoHideDuration={6000} 
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}